import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AUTO_ASSIGN } from '@/constants/routes';
import { CARD_ICONS } from '../Carriers/constants';
import SettingCard from '../Carriers/ExpandedCarrier/SettingCard';
import ShipperSettingModals from '../Carriers/ExpandedCarrier/ShipperSettingModals';
import { MARKETPLACE_ROW, NO_DEFAULT_PRICING } from '../Carriers/utils';
import { CopyableId } from '../modals/blocks';
import { needsSetupDraft, setupDraftFor, setupDraftKeyFor } from '../modals/IntegrationsModal';
import { CorpAdminContext } from '../store';

const SECTION_BLOCK_CLASS = 'flex flex-col gap-4 rounded-lg border border-slate-200 p-4';
const NOTE_CLASS = 'text-[13px] text-slate-500';

const ExpandedShipper = ({
    carrierSetups = [],
    marketplaceShipper,
    defaultIntegrationsByCarrier,
    defaultPricingByCarrier,
    loading,
    error,
}) => {
    const navigate = useNavigate();
    const { callbacks } = useContext(CorpAdminContext);
    const [openModal, setOpenModal] = useState(null);

    let setups = carrierSetups;
    if (marketplaceShipper) {
        const marketplace = {
            carrierRow: MARKETPLACE_ROW,
            shipper: marketplaceShipper,
            cards: marketplaceShipper.cards,
        };
        setups = [...carrierSetups, marketplace];
    }

    const openCard = (card, { carrierRow, shipper }) => {
        if (card.key === 'delivery_profile') {
            navigate(AUTO_ASSIGN);
            return;
        }

        const defaultIntegrations = defaultIntegrationsByCarrier[carrierRow.client_id] || [];
        const needsIntegration =
            card.key === 'integration' && needsSetupDraft(shipper.settings.integrations, defaultIntegrations);
        if (needsIntegration) {
            const draftKey = setupDraftKeyFor(shipper.client_id, carrierRow.client_id);
            const draft = setupDraftFor(shipper.client_id, carrierRow.client_id);
            // An open draft may hold the id of a row a failed save wrote, so reopening keeps it.
            callbacks.setCustomizeDrafts((drafts) => (drafts[draftKey] ? drafts : { ...drafts, [draftKey]: draft }));
        }

        setOpenModal({ cardKey: card.key, carrierId: carrierRow.client_id });
    };

    // Looked up on every render so a modal reflects the store after its own mutation lands.
    const modalSetup = setups.find((setup) => setup.carrierRow.client_id === openModal?.carrierId);
    const settingModal = modalSetup ? (
        <ShipperSettingModals
            row={modalSetup.carrierRow}
            cardKey={openModal.cardKey}
            shipper={modalSetup.shipper}
            carrierName={modalSetup.carrierRow.business_name}
            defaultPricing={defaultPricingByCarrier[modalSetup.carrierRow.client_id] || NO_DEFAULT_PRICING}
            defaultIntegrations={defaultIntegrationsByCarrier[modalSetup.carrierRow.client_id] || []}
            appliedPricing={modalSetup.appliedPricing}
            // A carrier's default covers every shipper it serves, so it is only edited on the Carriers tab.
            hideDefaults
            onClose={() => setOpenModal(null)}
        />
    ) : null;

    let carrierSections = setups.map((setup) => (
        <div key={setup.carrierRow.client_id} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold text-slate-900">{setup.carrierRow.business_name}</span>
                {setup.carrierRow.isMarketplace ? null : (
                    <CopyableId label="Carrier ID" value={setup.carrierRow.client_id} />
                )}
            </div>
            <div className="grid grid-cols-4 gap-4">
                {setup.cards.map((card) => (
                    <SettingCard
                        key={card.key}
                        card={card}
                        icon={CARD_ICONS[card.key]}
                        onAction={() => openCard(card, setup)}
                    />
                ))}
            </div>
        </div>
    ));
    if (!setups.length) carrierSections = <span className={NOTE_CLASS}>No carriers work with this shipper yet.</span>;
    if (loading) carrierSections = <span className={NOTE_CLASS}>Loading carriers…</span>;
    if (error) carrierSections = <span className={NOTE_CLASS}>Carriers could not be loaded.</span>;

    return (
        <div className="flex flex-col gap-4 bg-white px-5 py-4">
            <div className={SECTION_BLOCK_CLASS}>
                <span className="text-[11px] font-bold uppercase text-slate-500">
                    Carriers working with this shipper
                </span>
                <span className={NOTE_CLASS}>
                    Carriers in your organization&apos;s network. Carrier pricing shows the final mile rate that
                    applies.
                </span>
                {carrierSections}
            </div>

            {settingModal}
        </div>
    );
};

export default ExpandedShipper;
